/**
 * The walkthrough's narration, uploaded while it is still being recorded
 * (TOADO-901), so each moment can be transcribed seconds after it ends.
 *
 * One audio-only `MediaRecorder` on the microphone for the whole walkthrough,
 * paused and resumed in the same calls as the master so the two share the
 * logical timeline. Its chunks are sent in order, append-only by byte offset
 * (`PUT /recordings/:id/live-audio`). The server cuts each moment from the
 * track once the moment is final, because only it knows where a moment ends.
 *
 * Kept in memory, not in IndexedDB. Live audio never outlives the page that
 * records it: a resume after a crash starts a new part, and the server takes
 * live audio for the first part only. Everything here is an accelerator; when
 * it fails, or is refused, the master serves every moment after Stop as it
 * always did, so a failure only ever closes the track, never the recording.
 */

/** What the server said about one part. */
export type LiveAudioPartResult =
  /** Taken; the acknowledged track now ends at `offset`. */
  | { kind: 'acknowledged'; offset: number }
  /** Not taken here: the acknowledged track ends at `offset`. */
  | { kind: 'resume'; offset: number }
  /** This walkthrough takes no live audio (stopped, or transcription is off). */
  | { kind: 'closed' }
  /** Not known: unreachable, or a server error. Send the same part again. */
  | { kind: 'retry' };

export interface LiveAudioServerApi {
  putLiveAudio(recordingId: string, part: {
    offset: number;
    bytes: Uint8Array;
    /** How far into the walkthrough (logical ms) the track reaches with this part. */
    coveredMs: number;
    /** Where on that timeline the track starts; the first part only. */
    originMs?: number;
  }): Promise<LiveAudioPartResult>;
}

/** Narration at speech quality: about 4 KB a second. */
export const LIVE_AUDIO_BITS_PER_SECOND = 32_000;
/** How often the recorder hands over a chunk. */
export const LIVE_AUDIO_TIMESLICE_MS = 2_000;
/** One request's largest body, under the server's 256 KiB. */
export const LIVE_AUDIO_BATCH_MAX_BYTES = 192 * 1024;
/**
 * How much may wait unsent before the track gives up: about eight minutes of
 * narration. A server unreachable that long is better served by the master.
 */
export const LIVE_AUDIO_MAX_BUFFERED_BYTES = 2 * 1024 * 1024;
/** How long the last chunk may take to arrive after Stop. */
const FINAL_CHUNK_WAIT_MS = 2_000;
const RETRY_MIN_MS = 1_000;
const RETRY_MAX_MS = 15_000;
/** Unknown outcomes in a row before the track gives up: about four minutes. */
export const LIVE_AUDIO_MAX_RETRIES = 20;

export type LiveAudioTrackDependencies = {
  recordingId: string;
  server: LiveAudioServerApi;
  stream: MediaStream;
  /** The master's logical timeline, in ms. */
  timelineMs: () => number;
  mimeType: string;
  createRecorder?: (stream: MediaStream, options: MediaRecorderOptions) => MediaRecorder;
  sleep?: (ms: number) => Promise<void>;
  /** Told once, with why, when the track gives up by itself (never for close()). */
  onGiveUp?: (reason: string) => void;
};

type Chunk = { bytes: Uint8Array; coveredMs: number };

export class LiveAudioTrack {
  private recorder: MediaRecorder | null = null;
  private originMs = 0;
  /** Chunks not yet acknowledged, oldest first. */
  private pending: Chunk[] = [];
  private pendingBytes = 0;
  /** Where the acknowledged track ends. */
  private acknowledged = 0;
  /** The part being sent, fixed until the server answers it for certain. */
  private inFlight: { bytes: Uint8Array; coveredMs: number; chunks: number } | null = null;
  private reads: Promise<void> = Promise.resolve();
  private pumping: Promise<void> | null = null;
  private closed = false;
  private stopped: Promise<void> | null = null;

  constructor(private readonly deps: LiveAudioTrackDependencies) {}

  /** Whether the track has given up, or was refused. */
  get isClosed(): boolean {
    return this.closed;
  }

  /** Start beside the master. False, and closed, when this browser cannot. */
  start(): boolean {
    try {
      const options: MediaRecorderOptions = {
        ...(this.deps.mimeType ? { mimeType: this.deps.mimeType } : {}),
        audioBitsPerSecond: LIVE_AUDIO_BITS_PER_SECOND,
      };
      const recorder = this.deps.createRecorder?.(this.deps.stream, options)
        ?? new MediaRecorder(this.deps.stream, options);
      recorder.addEventListener('dataavailable', (event: BlobEvent) => this.take(event.data));
      recorder.addEventListener('error', () => this.giveUp('recorder_error'));
      this.recorder = recorder;
      this.originMs = Math.max(0, Math.round(this.deps.timelineMs()));
      recorder.start(LIVE_AUDIO_TIMESLICE_MS);
      return true;
    } catch {
      this.close(); // The caller reports a track that could not start.
      return false;
    }
  }

  pause(): void {
    if (this.closed || this.recorder?.state !== 'recording') return;
    try {
      this.recorder.pause();
    } catch {
      // A track that cannot pause would drift off the timeline: give it up.
      this.giveUp('pause_failed');
    }
  }

  resume(): void {
    if (this.closed || this.recorder?.state !== 'paused') return;
    try {
      this.recorder.resume();
    } catch {
      this.giveUp('resume_failed');
    }
  }

  /**
   * At Stop: take the last chunk and send what is left. Never waits on the
   * server for the recording's sake; the caller need not await it.
   */
  finish(): Promise<void> {
    if (this.stopped) return this.stopped;
    this.stopped = (async () => {
      const recorder = this.recorder;
      if (recorder && recorder.state !== 'inactive' && !this.closed) {
        const done = new Promise<void>((resolve) => {
          recorder.addEventListener('stop', () => resolve(), { once: true });
          setTimeout(resolve, FINAL_CHUNK_WAIT_MS);
        });
        try {
          recorder.stop();
        } catch {
          // Stopped already.
        }
        await done;
      }
      await this.reads;
      while (this.pumping && !this.closed) await this.pumping;
    })();
    return this.stopped;
  }

  /** Gives up by itself, and says why: the master serves every moment after Stop. */
  private giveUp(reason: string): void {
    if (this.closed) return;
    this.close();
    try {
      this.deps.onGiveUp?.(reason);
    } catch {
      // Only a report.
    }
  }

  /** Give the track up: nothing more is recorded or sent. */
  close(): void {
    this.closed = true;
    this.pending = [];
    this.pendingBytes = 0;
    this.inFlight = null;
    try {
      if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
    } catch {
      // Already stopped.
    }
  }

  private take(data: Blob): void {
    if (this.closed || data.size === 0) return;
    // Read now, in order: the timeline at this moment is how far the chunk reaches.
    const coveredMs = Math.max(this.originMs, Math.round(this.deps.timelineMs()));
    this.reads = this.reads.then(async () => {
      const bytes = new Uint8Array(await data.arrayBuffer());
      if (this.closed) return;
      this.pending.push({ bytes, coveredMs });
      this.pendingBytes += bytes.length;
      if (this.pendingBytes > LIVE_AUDIO_MAX_BUFFERED_BYTES) {
        this.giveUp('backlog');
        return;
      }
      this.kick();
    }).catch(() => this.giveUp('read_failed'));
  }

  private kick(): void {
    if (this.pumping || this.closed) return;
    this.pumping = this.drain().catch(() => this.giveUp('send_failed')).finally(() => {
      this.pumping = null;
      if (!this.closed && this.pending.length > 0) this.kick();
    });
  }

  private nextPart(): { bytes: Uint8Array; coveredMs: number; chunks: number } {
    let size = 0;
    let count = 0;
    for (const chunk of this.pending) {
      if (count > 0 && size + chunk.bytes.length > LIVE_AUDIO_BATCH_MAX_BYTES) break;
      size += chunk.bytes.length;
      count += 1;
    }
    const bytes = new Uint8Array(size);
    let at = 0;
    for (const chunk of this.pending.slice(0, count)) {
      bytes.set(chunk.bytes, at);
      at += chunk.bytes.length;
    }
    return { bytes, coveredMs: this.pending[count - 1]!.coveredMs, chunks: count };
  }

  private async drain(): Promise<void> {
    const sleep = this.deps.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
    let backoff = RETRY_MIN_MS;
    let retries = 0;
    while (!this.closed && this.pending.length > 0) {
      const part = this.inFlight ?? this.nextPart();
      this.inFlight = part;
      let result: LiveAudioPartResult;
      try {
        result = await this.deps.server.putLiveAudio(this.deps.recordingId, {
          offset: this.acknowledged,
          bytes: part.bytes,
          coveredMs: part.coveredMs,
          ...(this.acknowledged === 0 ? { originMs: this.originMs } : {}),
        });
      } catch {
        result = { kind: 'retry' };
      }
      if (this.closed) return;
      if (result.kind === 'closed') {
        this.giveUp('refused');
        return;
      }
      if (result.kind === 'retry') {
        // A server out of reach this long is better served by the master.
        if (++retries > LIVE_AUDIO_MAX_RETRIES) {
          this.giveUp('unreachable');
          return;
        }
        // The same part again, byte for byte: if it was taken and the answer
        // was lost, the server recognises it rather than seeing a conflict.
        await sleep(backoff);
        backoff = Math.min(backoff * 2, RETRY_MAX_MS);
        continue;
      }
      backoff = RETRY_MIN_MS;
      retries = 0;
      this.inFlight = null;
      const before = this.acknowledged;
      this.advanceTo(result.offset);
      // An answer that takes nothing would be asked again at once, for ever.
      if (!this.closed && this.acknowledged === before) this.giveUp('no_progress');
    }
  }

  /** The server's track ends at `offset`: drop what it holds, or give up if it holds less than was sent. */
  private advanceTo(offset: number): void {
    if (offset < this.acknowledged) {
      this.giveUp('resume_behind');
      return;
    }
    let drop = offset - this.acknowledged;
    while (drop > 0 && this.pending.length > 0) {
      const head = this.pending[0]!;
      if (head.bytes.length > drop) {
        // The server ends inside a chunk: something other than this track
        // wrote there. Nothing sent from here could line up again.
        this.giveUp('misaligned');
        return;
      }
      drop -= head.bytes.length;
      this.pendingBytes -= head.bytes.length;
      this.pending.shift();
    }
    if (drop > 0) {
      this.giveUp('misaligned');
      return;
    }
    this.acknowledged = offset;
  }
}
